import { useEffect, useState } from "react";

// =============================================================================
// useIdlePrefetchGate — defer background cache-warming off the page's dime
// =============================================================================
// The AppLayout prefetch chains (DM analytics, dashboard, calendar) fire dozens
// of heavy RPCs on EVERY pageload. The database is a 2-core Micro: those RPCs
// queue ahead of whatever the user's current page is fetching, so page-local
// requests that cost ≤30ms server-side were observed taking 1.5–10.3s waiting
// in line (S55 benchmark, Jun 12 2026: identical calendar request 10.3s with
// the storm running vs 0.3s without).
//
// This gate returns false for `delayMs` after mount, then waits for a browser
// idle slot before flipping true. Mount the prefetch hooks behind it
// (`{ready && <Warmer />}`) — an unmounted hook can't fire its one-shot warm
// OR register its event listeners, so every trigger path is gated at once
// without touching the hooks' internals.
//
// requestIdleCallback is feature-detected: Safari still doesn't ship it, so we
// fall through to flipping ready directly off the timer there. The 5s rIC
// timeout caps how long a busy main thread can hold the warm-up hostage.
// =============================================================================

export const PREFETCH_IDLE_DELAY_MS = 20_000;

export function useIdlePrefetchGate(delayMs: number = PREFETCH_IDLE_DELAY_MS): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    let idleHandle: number | null = null;

    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(
          () => {
            if (!cancelled) setReady(true);
          },
          { timeout: 5_000 }
        );
      } else {
        setReady(true);
      }
    }, delayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (idleHandle !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
    };
  }, [delayMs, ready]);

  return ready;
}
